/**
 * TSG Nexus feature billing — API client.
 *
 * One place that knows the backend's billing routes
 * (/api/nextgen-builder/billing/*) and how to identify the builder:
 *
 *   Authorization: Bearer <session_token>   (issued at Nexus login; REQUIRED
 *                                            for cart / checkout / orders)
 *   x-builder-id: <builder id>              (legacy identity; enough for the
 *                                            read-only catalog/summary)
 *
 * Nothing here trusts a price or quota from the client: every response is
 * the server's computed state, and the UI only renders it.
 */
import axios from 'axios';
import API_BASE_URL from '../../Config/config';

export const BILLING_BASE = `${API_BASE_URL}/api/nextgen-builder/billing`;
export const NEXUS_AUTH_EVENT = 'nexus-billing:auth-required';
export const SESSION_TOKEN_KEY = 'session_token';

export class BillingApiError extends Error {
  constructor(message, { status = 0, code = 'NETWORK_ERROR', payload = {} } = {}) {
    super(message);
    this.name = 'BillingApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  get purchaseRequired() { return !!this.payload.purchaseRequired; }
}

const getBuilderId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u && u.id != null ? String(u.id) : null;
  } catch (_) {
    return null;
  }
};

export const getSessionToken = () => {
  try { return localStorage.getItem(SESSION_TOKEN_KEY) || null; } catch (_) { return null; }
};

export const authHeaders = () => {
  const h = {};
  const id = getBuilderId();
  if (id) h['x-builder-id'] = id;
  const token = getSessionToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

/** Normalise any axios failure into a BillingApiError with the backend's code/payload. */
export const toBillingError = (err) => {
  if (err instanceof BillingApiError) return err;
  const res = err && err.response;
  if (res && res.data && typeof res.data === 'object') {
    const { message, code, ...payload } = res.data;
    const e = new BillingApiError(message || 'Request failed', { status: res.status, code: code || 'REQUEST_FAILED', payload });
    if (res.status === 401 && (code === 'NEXUS_AUTH_REQUIRED' || code === 'NEXUS_AUTH_INVALID')) {
      try { window.dispatchEvent(new CustomEvent(NEXUS_AUTH_EVENT, { detail: { code } })); } catch (_) { /* ignore */ }
    }
    return e;
  }
  return new BillingApiError(err && err.message ? err.message : 'Network error', { status: res ? res.status : 0 });
};

const request = async (method, path, { data, params, responseType } = {}) => {
  try {
    const res = await axios({ method, url: `${BILLING_BASE}${path}`, data, params, responseType, headers: authHeaders() });
    return res.data && Object.prototype.hasOwnProperty.call(res.data, 'data') ? res.data.data : res.data;
  } catch (err) {
    throw toBillingError(err);
  }
};

export const billingApi = {
  getCatalog: () => request('get', '/catalog'),
  getSummary: () => request('get', '/summary'),
  getFeature: (code, technology) => request('get', `/features/${code}`, { params: technology ? { technology } : undefined }),

  getCart: () => request('get', '/cart'),
  addToCart: ({ productCode, technologySlug = null, quantity = 1 }) => request('post', '/cart/items', { data: { productCode, technologySlug, quantity } }),
  updateCartItem: (itemId, quantity) => request('patch', `/cart/items/${itemId}`, { data: { quantity } }),
  removeCartItem: (itemId) => request('delete', `/cart/items/${itemId}`),
  clearCart: () => request('delete', '/cart'),

  checkout: ({ idempotencyKey } = {}) => request('post', '/checkout', { data: { acceptTerms: true, idempotencyKey } }),
  verifyPayment: (razorpayResponse) => request('post', '/payments/verify', { data: razorpayResponse }),
  reportPaymentFailure: (details) => request('post', '/payments/failed', { data: details }),

  listOrders: (params) => request('get', '/orders', { params }),
  getOrder: (id) => request('get', `/orders/${id}`),
  /** Blob of the invoice PDF (API Gateway needs Accept: application/pdf to base64-decode the body). */
  downloadInvoice: async (id) => {
    try {
      const res = await axios.get(`${BILLING_BASE}/orders/${id}/invoice`, { responseType: 'blob', headers: { ...authHeaders(), Accept: 'application/pdf' } });
      return res.data;
    } catch (err) {
      if (err.response && err.response.data instanceof Blob) {
        try { const text = await err.response.data.text(); err.response.data = JSON.parse(text); } catch (_) { /* keep blob */ }
      }
      throw toBillingError(err);
    }
  },

  admin: {
    getConfig: (userId) => axios.get(`${BILLING_BASE}/admin/config`, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    update: (userId, table, key, patch) => axios.patch(`${BILLING_BASE}/admin/config/${table}/${encodeURIComponent(key)}`, patch, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    createTier: (userId, tier) => axios.post(`${BILLING_BASE}/admin/tiers`, tier, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    deleteTier: (userId, id) => axios.delete(`${BILLING_BASE}/admin/tiers/${id}`, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    getAudit: (userId, limit = 100) => axios.get(`${BILLING_BASE}/admin/audit`, { params: { limit }, headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    getOrders: (userId, limit = 100) => axios.get(`${BILLING_BASE}/admin/orders`, { params: { limit }, headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    grant: (userId, body) => axios.post(`${BILLING_BASE}/admin/entitlements/grant`, body, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    syncElitePages: (userId, slug) => axios.post(`${BILLING_BASE}/admin/elite-pages/${encodeURIComponent(slug)}/sync`, {}, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    /** Feature Pricing console (/superadmin/feature-pricing) */
    getFeaturePricing: (userId) => axios.get(`${BILLING_BASE}/admin/feature-pricing`, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    updateFeaturePricing: (userId, featureCode, body) => axios.put(`${BILLING_BASE}/admin/feature-pricing/${encodeURIComponent(featureCode)}`, body, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
    updateTaxRate: (userId, body) => axios.put(`${BILLING_BASE}/admin/feature-pricing/tax`, body, { headers: { 'user-id': userId } }).then((r) => r.data.data).catch((e) => { throw toBillingError(e); }),
  },
};

/** "99900" → "₹999.00" — display only; the server owns every number. */
export const formatPaise = (paise, { symbol = '₹' } = {}) => {
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  const neg = n < 0 ? '-' : '';
  const abs = Math.abs(Math.round(n));
  const rupees = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  return `${neg}${symbol}${rupees.toLocaleString('en-IN')}.${frac}`;
};

export const FEATURE_META = {
  AI_TUTOR: { label: 'AI Tutor', unit: 'prompt', units: 'prompts', route: '/nextgen-builder/ai-tutor', color: '#6C3BFF' },
  MOCK_INTERVIEW: { label: 'Mock Interviews', unit: 'interview', units: 'interviews', route: '/nextgen-builder/mock-interview', color: '#06B6D4' },
  ELITE_MASTERY: { label: 'Elite Mastery', unit: 'page', units: 'pages', route: '/nextgen-builder/elite-mastery', color: '#F59E0B' },
};

/** "100 prompts" for a credit wallet, "Full access · 6 months" for a time-boxed line — from the server's own fields. */
export const describeLineUnits = (line, units = 'units') => {
  if (!line) return '';
  if (line.accessType === 'FULL_ACCESS') return `Full access${line.validityLabel ? ` · ${line.validityLabel}` : ''}`;
  const n = Number(line.unitsTotal);
  return `${Number.isFinite(n) ? n : line.unitsTotal} ${n === 1 ? units.replace(/s$/, '') : units}`;
};

/** True when the signed-in account is a TSG Nexus builder (reused learner components run in both worlds). */
export const isNexusBuilderSession = () => {
  try {
    const uid = localStorage.getItem('user_id') || '';
    if (uid.startsWith('nextgen_builder_')) return true;
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return !!(u && u.accountType === 'nextgen_builder');
  } catch (_) {
    return false;
  }
};

/** Identity header the reused learner endpoints (tsg, ai-services, elite) expect for a Nexus builder. */
export const nexusLearnerHeaders = () => {
  const h = {};
  try {
    const uid = localStorage.getItem('user_id');
    if (uid) h['user-id'] = uid;
  } catch (_) { /* ignore */ }
  const token = getSessionToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

/** Read the structured quota error off a fetch Response / axios error / BillingApiError. */
export const extractPurchaseRequired = (errOrBody) => {
  if (!errOrBody) return null;
  if (errOrBody instanceof BillingApiError) return errOrBody.purchaseRequired ? { code: errOrBody.code, message: errOrBody.message, ...errOrBody.payload } : null;
  const body = errOrBody.response && errOrBody.response.data ? errOrBody.response.data : errOrBody;
  if (body && typeof body === 'object' && body.purchaseRequired) return body;
  return null;
};

export default billingApi;
